import React from 'react';
import { RiCloseFill } from 'react-icons/ri';
import { useBannerDismiss } from '../../Hooks/useBannerDismiss';
import styles from './Banner.module.scss';
import supplierIntelligencePinkIcon from '../../../assets/images/supplierIntelligencePinkIcon.svg';
import supplierIntelligenceLightPinkIcon from '../../../assets/images/supplierIntelligenceLightPinkIcon.svg';
import MeritoButton from '../MeritoButtons/MeritoButtons';

export type BannerVariant = 'dark' | 'purple';

type Props = {
  id: string;
  highlightText: string;
  message: string;
  variant?: BannerVariant;
  icon?: React.ReactNode;
  dismissible?: boolean;
  action?: { label: string; onClick: () => void };
  className?: string;
  onDismiss?: () => void;
};

const defaultIcons: Record<string, string> = {
  dark: supplierIntelligencePinkIcon,
  purple: supplierIntelligenceLightPinkIcon,
};

const Banner = ({
  id,
  highlightText,
  message,
  variant = 'dark',
  icon,
  dismissible,
  action,
  className = '',
  onDismiss,
}: Props) => {
  const isDismissibleByDefault = variant === 'purple';
  const canDismiss = dismissible !== undefined ? dismissible : isDismissibleByDefault;

  const { isDismissed, isExiting, dismiss } = useBannerDismiss(id);

  const handleDismiss = () => {
    dismiss();
    onDismiss?.();
  };

  if (isDismissed) return null;

  const resolvedIcon = icon !== undefined ? icon : (
    <img src={defaultIcons[variant]} alt="" />
  );

  return (
    <div
      className={`${styles.banner} ${styles[variant]} ${isExiting ? styles.exit : styles.enter} ${className}`}
    >
      <div className={styles.content}>
        {resolvedIcon && <span className={styles.icon}>{resolvedIcon}</span>}
        <p className={styles.text}>
          <span className={styles.highlight}>{highlightText}</span>
          <span className={styles.body}>{message}</span>
        </p>
      </div>

      <div className={styles.actions}>
        {action && (
          variant === 'purple' ? (
            <MeritoButton
              type="primary-1"
              size="md"
              isRounded
              onClick={action.onClick}
              extraStyles={{ backgroundColor: '#FFFFFF' }}
            >
              <span style={{ color: '#212121' }}>{action.label}</span>
            </MeritoButton>
          ) : (
            <MeritoButton
              type="primary-1"
              size="md"
              isRounded
              btnText={action.label}
              onClick={action.onClick}
              extraStyles={{ backgroundColor: '#f73e77' }}
            />
          )
        )}
        {canDismiss && (
          <button className={styles.closeButton} onClick={handleDismiss} aria-label="Dismiss">
            <RiCloseFill size={17} />
          </button>
        )}
      </div>
    </div>
  );
};

export default Banner;
